import React, { useState, useMemo, useEffect } from 'react';
import { Table, Input, Select, Tag, Card, Row, Col, Space, Button, Tooltip, Statistic, Alert, message } from 'antd';
import { SearchOutlined, ReloadOutlined, ClockCircleOutlined, CheckCircleOutlined, WarningOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import {
  useListServerProcessesQuery,
  useListProjectsQuery,
  useAsignarProcesoProyectoMutation,
  useAplicarReglasMutation,
} from '../store/api';
import dayjs from 'dayjs';

const typeColors = {
  cron: 'blue',
  service: 'green',
  inotify: 'orange',
  reboot_script: 'purple',
  docker: 'cyan',
};

const statusColors = {
  ok: 'success',
  running: 'processing',
  error: 'error',
  missing: 'error',
  stopped: 'error',
  timeout: 'error',
  stale: 'warning',
  error_in_log: 'error',
  no_ssh: 'warning',
  unknown: 'default',
  warning: 'warning',
};

const statusLabels = {
  ok: 'OK',
  error: 'ERROR',
  missing: 'NO EXISTE',
  stopped: 'DETENIDO',
  timeout: 'TIMEOUT',
  stale: 'SIN ACTIVIDAD',
  error_in_log: 'ERROR EN LOG',
  no_ssh: 'SIN SSH',
  unknown: 'DESCONOCIDO',
  warning: 'ALERTA',
};

const ServerProcesses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ is_active: '1' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    const proyectoId = searchParams.get('proyecto_id');
    const sin = searchParams.get('sin_proyecto');
    setFilters((prev) => ({
      ...prev,
      ...(proyectoId ? { proyecto_id: proyectoId } : {}),
      ...(sin ? { sin_proyecto: '1' } : {}),
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const queryParams = useMemo(() => ({
    page,
    limit: 20,
    ...filters,
    ...(search ? { search } : {}),
  }), [page, filters, search]);

  const { data, isLoading, isFetching, refetch } = useListServerProcessesQuery(queryParams, {
    pollingInterval: 120000,
  });
  const { data: proyectos = [] } = useListProjectsQuery();
  const [asignar] = useAsignarProcesoProyectoMutation();
  const [aplicarReglas, { isLoading: aplicando }] = useAplicarReglasMutation();

  const rows = data?.data || [];
  const total = data?.total || 0;
  const filterOptions = data?.filterOptions || {};
  const summary = data?.summary || {};
  const failing = Number(summary.failing || 0);
  const unknown = Number(summary.unknown || 0);
  const noSsh = Number(summary.no_ssh || 0);

  const proyectoOptions = useMemo(
    () => (proyectos || []).map((p) => ({ label: p.nombre, value: p.id })),
    [proyectos],
  );

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => {
      const next = { ...prev, [key]: value || '' };
      if (key === 'sin_proyecto' && value) {
        delete next.proyecto_id;
      }
      if (key === 'proyecto_id' && value) {
        delete next.sin_proyecto;
      }
      return next;
    });
    const nextParams = new URLSearchParams(searchParams);
    if (key === 'proyecto_id') {
      if (value) nextParams.set('proyecto_id', value);
      else nextParams.delete('proyecto_id');
      nextParams.delete('sin_proyecto');
    }
    if (key === 'sin_proyecto') {
      if (value) nextParams.set('sin_proyecto', '1');
      else nextParams.delete('sin_proyecto');
      nextParams.delete('proyecto_id');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleAsignar = async (processId, proyectoId) => {
    try {
      await asignar({ id: processId, proyecto_id: proyectoId ?? '' }).unwrap();
      message.success(proyectoId ? 'Proyecto asignado' : 'Proyecto desvinculado');
      refetch();
    } catch {
      message.error('No se pudo asignar el proyecto');
    }
  };

  const handleAplicarReglas = async () => {
    try {
      const res = await aplicarReglas().unwrap();
      message.success(`Reglas aplicadas: ${res.asignados || 0} asignados, ${res.sin_asignar || 0} sin proyecto`);
      refetch();
    } catch {
      message.error('Error al aplicar reglas');
    }
  };

  const columns = [
    {
      title: 'Servidor',
      dataIndex: 'server_name',
      width: 170,
      render: (name, r) => (
        <Tooltip title={`${r.server_ip}${r.estado_servidor_nombre ? ` · ${r.estado_servidor_nombre}` : ''}`}>
          <span style={{ fontWeight: 500 }}>{name || r.estado_servidor_nombre || r.server_ip}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'process_type',
      width: 100,
      render: (t) => <Tag color={typeColors[t] || 'default'}>{t}</Tag>,
    },
    {
      title: 'Proyecto',
      dataIndex: 'proyecto_id',
      width: 220,
      render: (pid, r) => (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Select
            size="small"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Sin proyecto"
            style={{ width: '100%' }}
            value={pid || undefined}
            options={proyectoOptions}
            onChange={(v) => handleAsignar(r.id, v)}
          />
          {r.proyecto_origen ? (
            <Tag color={r.proyecto_origen === 'manual' ? 'purple' : 'blue'}>{r.proyecto_origen}</Tag>
          ) : null}
          {!pid && r.project ? <span style={{ fontSize: 11, color: '#8392a5' }}>texto: {r.project}</span> : null}
        </Space>
      ),
    },
    {
      title: 'Horario',
      dataIndex: 'schedule',
      width: 120,
      render: (s) => s ? <code style={{ fontSize: 12 }}>{s}</code> : '—',
    },
    {
      title: 'Descripción',
      dataIndex: 'description_human',
      ellipsis: true,
    },
    {
      title: 'Script',
      dataIndex: 'script_path',
      width: 200,
      ellipsis: true,
      render: (p) => p ? <code style={{ fontSize: 11 }}>{p}</code> : '—',
    },
    {
      title: 'Activo',
      dataIndex: 'is_active',
      width: 70,
      align: 'center',
      render: (v) => v ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'last_status',
      width: 110,
      render: (s) => <Tag color={statusColors[s] || 'default'}>{statusLabels[s] || s || '—'}</Tag>,
    },
    {
      title: 'Base de datos afectada',
      dataIndex: 'connects_to_db',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Escribe en',
      dataIndex: 'writes_to',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Origen',
      dataIndex: 'cron_source',
      width: 80,
      render: (v, r) => v ? <Tag>{v}{r.cron_user && r.cron_user !== 'root' ? `:${r.cron_user}` : ''}</Tag> : '—',
    },
    {
      title: 'Último check',
      dataIndex: 'last_run',
      width: 130,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—',
    },
    {
      title: 'Último log',
      dataIndex: 'last_log_seen',
      width: 130,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—',
    },
    {
      title: 'Avg (s)',
      dataIndex: 'execution_time_avg_sec',
      width: 70,
      align: 'right',
      render: (v) => v != null ? v : '—',
    },
  ];

  return (
    <div style={{ padding: '0 8px' }}>
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Procesos de Servidores</span>
            <Tag>{total} procesos</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button loading={aplicando} onClick={handleAplicarReglas}>Aplicar reglas ahora</Button>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={refetch}>Actualizar</Button>
          </Space>
        }
      >
        {(failing > 0 || noSsh > 0) && (
          <Alert
            type={failing > 0 ? 'error' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
            message={`${failing} proceso(s) con falla y ${noSsh} sin verificación SSH`}
            description="Los registros con error, detenidos o faltantes aparecen primero en la tabla."
          />
        )}

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total" value={Number(summary.total || total || 0)} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="OK" value={Number(summary.ok || 0)} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Fallas" value={failing} valueStyle={{ color: failing ? '#f5222d' : undefined }} prefix={<WarningOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Sin verificar" value={unknown + noSsh} prefix={<QuestionCircleOutlined />} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="Buscar..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Servidor"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('server_ip', v)}
              options={(filterOptions.server_ip || []).map(ip => ({ label: ip, value: ip }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Proyecto (vinculado)"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={filters.proyecto_id || undefined}
              onChange={(v) => handleFilterChange('proyecto_id', v)}
              options={proyectoOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Sin proyecto"
              allowClear
              style={{ width: '100%' }}
              value={filters.sin_proyecto || undefined}
              onChange={(v) => handleFilterChange('sin_proyecto', v)}
              options={[{ label: 'Solo sin proyecto', value: '1' }]}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Tipo"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('process_type', v)}
              options={(filterOptions.process_type || []).map(t => ({ label: t, value: t }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Estado"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('last_status', v)}
              options={(filterOptions.last_status || []).map(s => ({ label: s, value: s }))}
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Select
              placeholder="Activo"
              allowClear
              style={{ width: '100%' }}
              value={filters.is_active || undefined}
              onChange={(v) => handleFilterChange('is_active', v)}
              options={[{ label: 'Sí', value: '1' }, { label: 'No', value: '0' }]}
            />
          </Col>
        </Row>

        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          scroll={{ x: 1400 }}
          rowClassName={(record) => (
            ['error', 'missing', 'stopped', 'timeout', 'error_in_log', 'stale'].includes(record.last_status) ? 'row-error' : ''
          )}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (t, range) => `${range[0]}-${range[1]} de ${t}`,
            onChange: (p) => setPage(p),
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '8px 0', fontSize: 13 }}>
                <Row gutter={[16, 8]}>
                  <Col span={12}><strong>Script:</strong> <code>{record.script_path || '—'}</code></Col>
                  <Col span={12}><strong>Log:</strong> <code>{record.log_path || '—'}</code></Col>
                  <Col span={12}><strong>Conecta a DB:</strong> {record.connects_to_db || '—'}</Col>
                  <Col span={12}><strong>Conecta a servidor:</strong> {record.connects_to_server || '—'}</Col>
                  <Col span={12}><strong>Escribe en:</strong> {record.writes_to || '—'}</Col>
                  <Col span={12}><strong>Usa lock:</strong> {record.uses_lock ? 'Sí' : 'No'}</Col>
                  <Col span={12}><strong>Riesgo overlap:</strong> {record.overlap_risk || '—'}</Col>
                  <Col span={12}><strong>Tiempo avg:</strong> {record.execution_time_avg_sec != null ? `${record.execution_time_avg_sec}s` : '—'}</Col>
                  <Col span={12}><strong>Origen vínculo:</strong> {record.proyecto_origen || '—'}</Col>
                  <Col span={12}><strong>Proyecto:</strong> {record.proyecto_nombre || '—'}</Col>
                  {record.last_error && (
                    <Col span={24}><strong>Último error:</strong> <code style={{ color: 'red' }}>{record.last_error}</code></Col>
                  )}
                  {record.notes && (
                    <Col span={24}><strong>Notas:</strong> {record.notes}</Col>
                  )}
                </Row>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default ServerProcesses;
